export const DEFAULT_SITE_ORIGIN = "https://www.kslau.com.my";

export const DEFAULT_OG_IMAGE_PATH = "/og-image.jpg";
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const SITE_NAME = "KS Lau & Co";

export const DEFAULT_SITE_TITLE = "KS Lau & Co — Chartered Accountants, Penang";
export const DEFAULT_HOME_TITLE = "KS Lau & Co — Chartered Accountants";
export const DEFAULT_SITE_DESCRIPTION =
  "KS Lau & Co, Penang chartered accountants since 1978. Partner-led tax, audit, advisory and ESG services for family businesses and corporates across generations.";

/** Optional X handle from VITE_TWITTER_SITE, e.g. @KSLauCo */
export function getTwitterSite(): string | undefined {
  const handle = import.meta.env.VITE_TWITTER_SITE;
  const trimmed = String(handle ?? "").trim();
  return trimmed || undefined;
}

export const PUBLIC_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

export const ADMIN_ROBOTS = "noindex, nofollow";

export function resolveSiteOrigin(siteUrl?: string | null): string {
  const raw = String(siteUrl ?? "").trim().replace(/\/$/, "");
  if (!raw) return DEFAULT_SITE_ORIGIN;
  if (raw === "https://kslau.com" || raw === "https://kslau.com.my") {
    return DEFAULT_SITE_ORIGIN;
  }
  return raw;
}

/** Absolute canonical URL for a pathname on this site. */
export function buildCanonicalUrl(origin: string, pathname: string): string {
  const path = pathname || "/";
  if (path === "/") return `${origin}/`;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function absoluteAssetUrl(origin: string, assetPath: string): string {
  return `${origin}${assetPath.startsWith("/") ? assetPath : `/${assetPath}`}`;
}

/** Resolve upload or relative media paths to an absolute share image URL. */
export function absoluteMediaUrl(origin: string, src?: string | null): string | undefined {
  if (!src?.trim()) return undefined;
  const value = src.trim();
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return absoluteAssetUrl(origin, value);
}

export function defaultOgImageUrl(origin: string): string {
  return absoluteAssetUrl(origin, DEFAULT_OG_IMAGE_PATH);
}

type HeadMeta = { title?: string; name?: string; property?: string; content?: string; charSet?: string };

export function buildOpenGraphMeta(args: {
  siteUrl?: string | null;
  pathname: string;
  title: string;
  description: string;
  type?: string;
  image?: string | null;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
}): HeadMeta[] {
  const origin = resolveSiteOrigin(args.siteUrl);
  const url = buildCanonicalUrl(origin, args.pathname);
  const image = args.image ?? defaultOgImageUrl(origin);

  return [
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "en_MY" },
    { property: "og:title", content: args.title },
    { property: "og:description", content: args.description },
    { property: "og:type", content: args.type ?? "website" },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:width", content: String(args.imageWidth ?? OG_IMAGE_WIDTH) },
    { property: "og:image:height", content: String(args.imageHeight ?? OG_IMAGE_HEIGHT) },
    {
      property: "og:image:alt",
      content: args.imageAlt ?? `${SITE_NAME} — Chartered Accountants, Penang`,
    },
  ];
}

export function buildTwitterMeta(args: {
  siteUrl?: string | null;
  title: string;
  description: string;
  image?: string | null;
  imageAlt?: string;
  card?: "summary" | "summary_large_image";
  site?: string | null;
}): HeadMeta[] {
  const origin = resolveSiteOrigin(args.siteUrl);
  const image = args.image ?? defaultOgImageUrl(origin);
  const site = args.site ?? getTwitterSite();

  return [
    { name: "twitter:card", content: args.card ?? "summary_large_image" },
    ...(site ? [{ name: "twitter:site", content: site }] : []),
    { name: "twitter:title", content: args.title },
    { name: "twitter:description", content: args.description },
    { name: "twitter:image", content: image },
    {
      name: "twitter:image:alt",
      content: args.imageAlt ?? `${SITE_NAME} — Chartered Accountants, Penang`,
    },
  ];
}
type HeadLink = {
  rel: string;
  href: string;
  hrefLang?: string;
  as?: string;
  type?: string;
  sizes?: string;
  crossOrigin?: string;
  fetchPriority?: string;
};

export function buildPublicIndexHead(args: {
  siteUrl?: string | null;
  pathname: string;
}): { meta: HeadMeta[]; links: HeadLink[] } {
  const pathname = args.pathname || "/";

  if (pathname.startsWith("/admin")) {
    return {
      meta: [{ name: "robots", content: ADMIN_ROBOTS }],
      links: [],
    };
  }

  const origin = resolveSiteOrigin(args.siteUrl);
  const canonical = buildCanonicalUrl(origin, pathname);

  return {
    meta: [{ name: "robots", content: PUBLIC_ROBOTS }],
    links: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", hrefLang: "en-MY", href: canonical },
      { rel: "alternate", hrefLang: "x-default", href: canonical },
    ],
  };
}

export function pageUrl(pathname: string, siteUrl?: string | null): string {
  return buildCanonicalUrl(resolveSiteOrigin(siteUrl), pathname);
}
